import * as React from 'react';

import { useTranslation } from 'react-i18next';

import {
  Accordion as AccordionRoot,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Form-section accordion used across all integration settings pages.
 *
 * Composes the shadcn `ui/accordion` Radix primitives into the domain pattern:
 * a single bordered card with a title/subtitle header, content body, and a
 * footer with optional secondary button and a primary "Save" button.
 *
 * Each consumer drives expansion via `expanded` + `handleChange` (controlled).
 */
export interface AccordionProps {
  children?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: string;
  onSubmit?: () => void;
  footerText?: string;
  disabled?: boolean;
  isLoading?: boolean;
  expanded?: boolean;
  handleChange?: (event: React.SyntheticEvent | null, isExpanded: boolean) => void;
  disableFooter?: boolean;
  nonExpandable?: boolean;
  readOnly?: boolean;
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  disableHover?: boolean;
}

const ITEM_VALUE = 'panel';

const Accordion = ({
  children,
  title,
  subtitle,
  onSubmit,
  footerText,
  disabled,
  isLoading,
  expanded,
  handleChange,
  disableFooter,
  nonExpandable,
  readOnly,
  secondaryButtonText,
  onSecondaryButtonClick,
  disableHover,
  ref,
}: AccordionProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      className={cn(
        'mb-4 w-xl scroll-mt-4 overflow-hidden rounded-md border border-border bg-background transition-colors duration-200',
        !disableHover && 'hover:border-ring',
        expanded && 'scroll-mt-9'
      )}
    >
      <AccordionRoot
        type="single"
        collapsible
        value={expanded ? ITEM_VALUE : ''}
        onValueChange={(value) => {
          if (nonExpandable) {
            return;
          }
          handleChange?.(null, value === ITEM_VALUE);
        }}
      >
        <AccordionItem value={ITEM_VALUE} className="border-b-0">
          <AccordionTrigger
            data-testid="expand-accordion"
            disabled={nonExpandable}
            className={cn(
              'rounded-none px-6 py-6 hover:no-underline data-[state=open]:border-b data-[state=open]:border-border',
              nonExpandable && 'cursor-default disabled:opacity-100 [&>svg]:hidden'
            )}
          >
            <div className="flex-1">
              {title && (
                <span className="block text-base font-semibold text-foreground">{title}</span>
              )}
              {subtitle && <span className="block text-sm text-foreground">{subtitle}</span>}
            </div>
          </AccordionTrigger>
          <AccordionContent className="p-0">
            <div className="p-6">{children}</div>
            {!disableFooter && (
              <div className="flex h-[62px] w-full items-center rounded-b-md border-t border-border bg-muted/50 px-4">
                {footerText && <span className="px-2 text-xs text-foreground">{footerText}</span>}
                <span
                  className={cn(
                    'flex flex-1 gap-2',
                    secondaryButtonText ? 'justify-between' : 'justify-end'
                  )}
                >
                  {secondaryButtonText && (
                    <Button
                      onClick={onSecondaryButtonClick}
                      variant="ghost"
                      disabled={readOnly}
                      className="text-destructive hover:text-destructive"
                    >
                      {secondaryButtonText}
                    </Button>
                  )}
                  <Button
                    onClick={onSubmit}
                    disabled={disabled || readOnly}
                    isLoading={isLoading}
                    data-testid="accordion-button"
                  >
                    {t('common.save')}
                  </Button>
                </span>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </AccordionRoot>
    </div>
  );
};

export default Accordion;
